import React, { useState, useEffect, useRef } from 'react';
import {
   Box,
   Paper,
   Typography,
   Avatar,
   Button,
   TextField,
   Grid,
   CircularProgress,
   Alert,
   Divider,
   Snackbar,
   Card,
   CardContent,
   Tabs,
   Tab,
   Chip,
   Container,
   IconButton,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   FormHelperText,
   InputAdornment,
   List,
   ListItem,
   ListItemIcon,
   ListItemText,
   Switch,
   FormControlLabel,
   Link,
   useMediaQuery,
   useTheme,
} from '@mui/material';
import {
   Save as SaveIcon,
   PhotoCamera as PhotoCameraIcon,
   Home as HomeIcon,
   Language as LanguageIcon,
   CreditCard as CreditCardIcon,
   CheckCircle as CheckCircleIcon,
   Cancel as CancelIcon,
   Edit as EditIcon,
   VerifiedUser as VerifiedUserIcon,
   Phone as PhoneIcon,
   Email as EmailIcon,
   Public as PublicIcon,
   Check as CheckIcon,
   AccessTime as TimeIcon,
   CameraAlt as CameraIcon,
   Facebook as FacebookIcon,
   Twitter as TwitterIcon,
   LinkedIn as LinkedInIcon,
   Instagram as InstagramIcon,
   Security as SecurityIcon,
   WorkOutline as WorkIcon,
   Person as PersonIcon,
   VpnKey as PasswordIcon,
   Notifications as NotificationsIcon,
   CreditCard as PaymentIcon,
   Share as ShareIcon,
   LocationOn as LocationIcon,
   Cake as BirthdayIcon,
   QrCode as QrCodeIcon,
   Download as DownloadIcon,
   Business as BusinessIcon,
   Description as DescriptionIcon,
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { getAuthHeaders } from '../../utils/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, getDoc, setDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { auth } from '../../firebase/config';
import { updateProfile as firebaseUpdateProfile } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { formatPhoneNumber, isValidPhoneNumber } from '../../utils/smsUtils';
import {
   getStripeInstance,
   SUBSCRIPTION_PRICES,
   createCheckoutSession,
   createPortalSession,
} from '../../utils/stripeUtils';
import { AIProfileService } from '../../services/AIProfileService';
import ProfilePhotos from './components/ProfilePhotos';
import ProfileNetwork from './components/ProfileNetwork';
import ProfileQRCode from './components/ProfileQRCode';
import ProfileBusinessCardTracking from './components/ProfileBusinessCardTracking';
import EditProfileDialog from './components/EditProfileDialog';
import FeedPage from './components/FeedPage';
import PhoneNumberLinking from './components/PhoneNumberLinking';
import LandingPageEditor from './components/LandingPageEditor';
import {
   useResponsive,
   useResponsiveTypography,
   useResponsiveSpacing,
} from '../../utils/responsive';

// Define interface for simplified user profile data
interface UserProfileExtended {
   bio?: string;
   location?: string;
   website?: string;
   phoneNumber?: string;
   enableSMS?: boolean;
   gender?: string;
   orientation?: string;
   relationshipStatus?: string;
   photoURL?: string;
   birthdate?: string;
   languages?: string;
   travelStyle?: string;
   accommodationPreference?: string;
   dietaryRestrictions?: string;
   coverPhotoURL?: string;
   title?: string;
   companyName?: string;
   tagline?: string;
   whatIDo?: Array<{
      id: string;
      emoji: string;
      title: string;
      subtitle: string;
      color: string;
      description: string;
   }>;
   interests?: Array<{
      id: string;
      emoji: string;
      name: string;
      selected: boolean;
      isCustom?: boolean;
   }>;
   lookingFor?: Array<{
      id: string;
      emoji: string;
      title: string;
      description: string;
   }>;
   featuredProjects?: Array<{
      id: string;
      title: string;
      description: string;
   }>;
   subscription?: {
      status: 'active' | 'canceled' | 'none';
      plan: string;
      amount: number;
      startDate: string;
      endDate: string;
   };
}

interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

const TabPanel = (props: TabPanelProps) => {
   const { children, value, index, ...other } = props;

   return (
      <div
         role='tabpanel'
         hidden={value !== index}
         id={`profile-tabpanel-${index}`}
         aria-labelledby={`profile-tab-${index}`}
         style={{
            width: '100%',
            height: '100%',
            overflow: 'auto',
         }}
         {...other}
      >
         {value === index && (
            <Box
               sx={{
                  p: 3,
                  width: '100%',
                  minHeight: '100%',
               }}
            >
               {children}
            </Box>
         )}
      </div>
   );
};

const Profile: React.FC = () => {
   const { currentUser, updateUserProfile } = useUser();
   // const { savePhoneNumber } = useNotifications();
   const navigate = useNavigate();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
   const { isMobile: responsiveIsMobile, deviceType } = useResponsive();
   const { fontSize, getFontSize } = useResponsiveTypography();
   const { spacing, getSpacing } = useResponsiveSpacing();

   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [successMessage, setSuccessMessage] = useState<string | null>(null);
   const [uploadProgress, setUploadProgress] = useState<number | null>(null);
   const [activeTab, setActiveTab] = useState(0);
   const [landingPageKey, setLandingPageKey] = useState(0);
   const fileInputRef = useRef<HTMLInputElement>(null);
   const coverPhotoInputRef = useRef<HTMLInputElement>(null);
   const [isEditing, setIsEditing] = useState(false);
   const [coverPhotoURL, setCoverPhotoURL] = useState<File | null>(null);
   const [downloading, setDownloading] = useState<string | null>(null);

   // Basic profile form data
   const [formData, setFormData] = useState<any>({
      displayName: currentUser?.displayName || '',
      email: currentUser?.email || '',
      photoURL: currentUser?.photoURL || '',
   });

   // Extended profile data (simplified)
   const [profileData, setProfileData] = useState<UserProfileExtended>({
      bio: '',
      location: '',
      website: '',
      phoneNumber: '',
      enableSMS: false,
      gender: '',
      orientation: '',
      relationshipStatus: '',
      birthdate: '',
      languages: '',
      travelStyle: '',
      accommodationPreference: '',
      dietaryRestrictions: '',
      photoURL: '',
      title: '',
      companyName: '',
      tagline: '',
      whatIDo: [],
      interests: [],
      lookingFor: [],
      featuredProjects: [],
      subscription: {
         status: 'none',
         plan: '',
         amount: 0,
         startDate: '',
         endDate: '',
      },
   });

   const [socialData, setSocialData] = useState({
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
   });

   // Add new state for dialog visibility
   const [editDialogOpen, setEditDialogOpen] = useState(false);

   // Fetch extended profile data
   useEffect(() => {
      const fetchProfileData = async () => {
         if (!currentUser?.uid) return;

         try {
            // Use backend API to fetch profile data for consistency
            const token = await auth.currentUser?.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/${currentUser.uid}`, {
               headers: {
                  Authorization: `Bearer ${token}`,
               },
            });

            if (response.ok) {
               const data = await response.json();
               console.log('Loading profile data from API:', data);
               console.log('PhotoURL from API:', data.photoURL);
               console.log('What I Do from DB:', data.whatIDo);
               console.log('Looking For from DB:', data.lookingFor);
               console.log('Featured Projects from DB:', data.featuredProjects);

               setProfileData({
                  ...data,
                  photoURL: data.photoURL || '',
                  bio: data.bio || '',
                  location: data.location || '',
                  website: data.website || '',
                  phoneNumber: data.phoneNumber || '',
                  enableSMS: data.enableSMS || false,
                  gender: data.gender || '',
                  orientation: data.orientation || '',
                  relationshipStatus: data.relationshipStatus || '',
                  birthdate: data.birthdate || '',
                  languages: data.languages || '',
                  travelStyle: data.travelStyle || '',
                  accommodationPreference: data.accommodationPreference || '',
                  dietaryRestrictions: data.dietaryRestrictions || '',
                  title: data.title || '',
                  companyName: data.companyName || '',
                  tagline: data.tagline || '',
                  whatIDo: (data.whatIDo || []).map((item: any) => ({
                     ...item,
                     description: item.description || '',
                  })),
                  interests: data.interests || [],
                  lookingFor: (data.lookingFor || []).map((item: any) => ({
                     ...item,
                     description: item.description || '',
                  })),
                  featuredProjects: (data.featuredProjects || []).map((item: any) => ({
                     ...item,
                     description: item.description || '',
                  })),
                  subscription: data.subscription || {
                     status: 'none',
                     plan: '',
                     amount: 0,
                     startDate: '',
                     endDate: '',
                  },
               });

               // Also set social data if available
               if (data.social) {
                  setSocialData({
                     facebook: data.social.facebook || '',
                     twitter: data.social.twitter || '',
                     linkedin: data.social.linkedin || '',
                     instagram: data.social.instagram || '',
                  });
               }
            } else {
               console.error('Failed to fetch profile data from API');
            }
         } catch (err) {
            console.error('Error fetching profile data:', err);
         }
      };

      fetchProfileData();
   }, [currentUser]);

   useEffect(() => {
      if (currentUser) {
         setFormData({
            displayName: currentUser.displayName || '',
            email: currentUser.email || '',
            photoURL: currentUser.photoURL || '',
         });
      }
   }, [currentUser]);

   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev: any) => ({
         ...prev,
         [name]: value,
      }));
   };

   // Handle direct field changes
   const handleDirectFieldChange = (field: string, value: any) => {
      setProfileData((prev) => ({
         ...prev,
         [field]: value,
      }));
   };

   const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
      // Refresh Landing Page Editor when switching to that tab
      if (newValue === 3) {
         setLandingPageKey((prev) => prev + 1);
      }
   };

   // Helper function to compress and optimize image
   const compressImage = (file: File, maxWidth: number = 800, quality: number = 0.9): Promise<Blob> => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
               const canvas = document.createElement('canvas');
               let { width, height } = img;

               // Maintain aspect ratio
               if (width > maxWidth) {
                  height = (height * maxWidth) / width;
                  width = maxWidth;
               }

               canvas.width = width;
               canvas.height = height;

               const ctx = canvas.getContext('2d');
               if (!ctx) {
                  reject(new Error('Failed to get canvas context'));
                  return;
               }

               // Use high-quality image smoothing
               ctx.imageSmoothingEnabled = true;
               ctx.imageSmoothingQuality = 'high';
               ctx.drawImage(img, 0, 0, width, height);

               canvas.toBlob(
                  (blob) => {
                     if (blob) {
                        resolve(blob);
                     } else {
                        reject(new Error('Failed to compress image'));
                     }
                  },
                  'image/jpeg',
                  quality
               );
            };
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = e.target?.result as string;
         };
         reader.onerror = () => reject(new Error('Failed to read file'));
         reader.readAsDataURL(file);
      });
   };

   // Handle file upload
   const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!currentUser || !e.target.files || e.target.files.length === 0) {
         return;
      }

      const file = e.target.files[0];
      setLoading(true);
      setError(null);

      try {
         // Compress image before upload
         const compressedImage = await compressImage(file, 800, 0.92);
         
         // Upload to Firebase Storage
         const storage = getStorage();
         const storageRef = ref(storage, `profile_photos/${currentUser.uid}`);

         // Upload compressed file
         await uploadBytes(storageRef, compressedImage, {
            contentType: 'image/jpeg',
            cacheControl: 'public, max-age=31536000',
         });

         // Get download URL
         const photoURL = await getDownloadURL(storageRef);

         // Call backend API to update profile and sync across all collections
         const token = await auth.currentUser?.getIdToken();
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/${currentUser.uid}`, {
            method: 'PUT',
            headers: {
               'Content-Type': 'application/json',
               Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ photoURL }),
         });

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update profile photo');
         }

         console.log('✅ Profile photo updated and synced across all collections');

         // Fetch fresh data from backend to sync everywhere
         const freshResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/${currentUser.uid}`, {
            headers: {
               Authorization: `Bearer ${token}`,
            },
         });

         if (freshResponse.ok) {
            const freshData = await freshResponse.json();
            // Force update all state with fresh data
            setFormData((prev: any) => ({
               ...prev,
               photoURL: freshData.photoURL,
               displayName: freshData.displayName || prev.displayName,
               email: freshData.email || prev.email,
            }));
            setProfileData((prev) => ({ ...prev, photoURL: freshData.photoURL }));
            console.log('✅ Profile data refreshed from backend');
         } else {
            // Fallback: Update with uploaded photoURL
         setFormData((prev: any) => ({ ...prev, photoURL }));
            setProfileData((prev) => ({ ...prev, photoURL }));
         }

         setSuccessMessage('Profile photo updated successfully');
      } catch (err) {
         console.error('Error uploading photo:', err);
         setError('Failed to upload profile photo');
      } finally {
         setLoading(false);
         setUploadProgress(null);

         // Reset file input
         if (fileInputRef.current) {
            fileInputRef.current.value = '';
         }
      }
   };

   const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      // Add null check for currentUser
      if (!currentUser) {
         setError('You must be logged in to update your profile');
         return;
      }

      setLoading(true);
      setError(null);

      try {
         // Update displayName in auth profile
         await firebaseUpdateProfile(auth.currentUser!, {
            displayName: formData.displayName,
         });

         // Debug: Log profile data being saved
         console.log('Saving profile data to Firestore:', profileData);
         console.log('What I Do data:', profileData.whatIDo);
         console.log('Looking For data:', profileData.lookingFor);
         console.log('Featured Projects data:', profileData.featuredProjects);

         // Update extended profile data in Firestore
         await setDoc(doc(db, 'userProfiles', currentUser!.uid), profileData, { merge: true });

         setSuccessMessage('Profile updated successfully');
      } catch (err) {
         console.error('Error updating profile:', err);
         setError('Failed to update profile');
      } finally {
         setLoading(false);
      }
   };

   const handleCloseSnackbar = () => {
      setSuccessMessage(null);
   };

   const handleSubscribe = async () => {
      try {
         setLoading(true);
         setError(null);

         // TEMPORARY SOLUTION: Create checkout session directly without waiting for Cloud Function
         await createCheckoutSession({
            priceId: SUBSCRIPTION_PRICES.yearly,
            successUrl: `${window.location.origin}/profile?tab=3&success=true&session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/profile?tab=3`,
            customerEmail: currentUser?.email || undefined,
         });

         // The function above will handle the redirect
      } catch (err) {
         console.error('Error initiating subscription:', err);
         setError((err as Error).message || 'Failed to process subscription. Please try again.');
         setLoading(false);
      }
   };

   // Check for subscription success
   useEffect(() => {
      // Check if returning from Stripe checkout
      const query = new URLSearchParams(window.location.search);
      const tabParam = query.get('tab');
      const sessionId = query.get('session_id');
      const success = query.get('success');

      if (tabParam) {
         setActiveTab(parseInt(tabParam, 10));
      }

      if (sessionId && success === 'true' && tabParam === '3') {
         setSuccessMessage('Subscription completed successfully!');

         // Refresh profile data to get updated subscription info
         if (currentUser?.uid) {
            getDoc(doc(db, 'users', currentUser.uid))
               .then((userDoc) => {
                  if (userDoc.exists()) {
                     const userData = userDoc.data();
                     if (userData.subscription) {
                        setProfileData((prev) => ({
                           ...prev,
                           subscription: userData.subscription,
                        }));
                     }
                  }
               })
               .catch((err) => {
                  console.error('Error fetching updated subscription info:', err);
               });
         }

         // Clear URL parameters
         window.history.replaceState({}, document.title, '/profile');
      }
   }, [currentUser]);

   const handleEditProfileClick = () => {
      setEditDialogOpen(true);
   };

   const handleEditDialogClose = async () => {
      setEditDialogOpen(false);

      // Refetch profile data from database after closing
      if (currentUser?.uid) {
         try {
            const profileDoc = await getDoc(doc(db, 'userProfile', currentUser.uid));
            if (profileDoc.exists()) {
               const data = profileDoc.data();
               setProfileData({
                  ...data,
                  photoURL: data.photoURL || '',
                  bio: data.bio || '',
                  location: data.location || '',
                  website: data.website || '',
                  phoneNumber: data.phoneNumber || '',
                  enableSMS: data.enableSMS || false,
                  gender: data.gender || '',
                  orientation: data.orientation || '',
                  relationshipStatus: data.relationshipStatus || '',
                  birthdate: data.birthdate || '',
                  languages: data.languages || '',
                  tagline: data.tagline || '',
                  title: data.title || '',
                  companyName: data.companyName || '',
                  whatIDo: data.whatIDo || [],
                  interests: data.interests || [],
                  lookingFor: data.lookingFor || [],
                  featuredProjects: data.featuredProjects || [],
               });

               // Update formData with display name
               setFormData((prev: any) => ({
                  ...prev,
                  displayName: data.displayName || currentUser.displayName || '',
                  photoURL: data.photoURL || currentUser.photoURL || '',
               }));
            }
         } catch (error) {
            console.error('Error refetching profile data:', error);
         }
      }
   };

   const handleProfileUpdated = async (updatedData: any) => {
      try {
         if (!currentUser) return;

         console.log('🔄 Starting profile refresh after update...');

         // Fetch fresh data from backend API
         const token = await auth.currentUser?.getIdToken();
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/${currentUser.uid}`, {
            headers: {
               Authorization: `Bearer ${token}`,
            },
         });

         if (response.ok) {
            const freshData = await response.json();
            console.log('✅ Got fresh profile data from backend:', freshData);

            // Force update formData
            setFormData({
               displayName: freshData.displayName || currentUser.displayName || '',
               email: freshData.email || currentUser.email || '',
               photoURL: freshData.photoURL || '',
            });

            // Force update profileData with complete fresh data
            setProfileData({
               bio: freshData.bio || '',
               location: freshData.location || '',
               website: freshData.website || '',
               phoneNumber: freshData.phoneNumber || '',
               enableSMS: freshData.enableSMS || false,
               gender: freshData.gender || '',
               orientation: freshData.orientation || '',
               relationshipStatus: freshData.relationshipStatus || '',
               birthdate: freshData.birthdate || '',
               languages: freshData.languages || '',
               travelStyle: freshData.travelStyle || '',
               accommodationPreference: freshData.accommodationPreference || '',
               dietaryRestrictions: freshData.dietaryRestrictions || '',
               photoURL: freshData.photoURL || '',
               title: freshData.title || '',
               companyName: freshData.companyName || '',
               tagline: freshData.tagline || '',
               whatIDo: (freshData.whatIDo || []).map((item: any) => ({
            ...item,
            description: item.description || '',
         })),
               interests: freshData.interests || [],
               lookingFor: (freshData.lookingFor || []).map((item: any) => ({
            ...item,
            description: item.description || '',
         })),
               featuredProjects: (freshData.featuredProjects || []).map((item: any) => ({
            ...item,
            description: item.description || '',
         })),
               subscription: freshData.subscription || {
                  status: 'none',
                  plan: '',
                  amount: 0,
                  startDate: '',
                  endDate: '',
               },
            });

            // Update social data
            setSocialData({
               facebook: freshData.facebook || '',
               twitter: freshData.twitter || '',
               linkedin: freshData.linkedin || '',
               instagram: freshData.instagram || '',
            });

            // Update Firebase Auth profile if photoURL changed
            if (freshData.photoURL && auth.currentUser && auth.currentUser.photoURL !== freshData.photoURL) {
               await firebaseUpdateProfile(auth.currentUser, {
                  photoURL: freshData.photoURL,
               });
               console.log('✅ Updated Firebase Auth photoURL');
            }

            // Show success message
            setSuccessMessage('Profile updated successfully');
            console.log('✅ Profile refresh complete');

            // Force a small delay to ensure state updates are applied
            await new Promise(resolve => setTimeout(resolve, 100));
         } else {
            throw new Error('Failed to fetch fresh profile data');
         }
      } catch (error) {
         console.error('❌ Error refreshing profile data:', error);
         
         // Fallback: Force update with passed data
         setFormData({
            displayName: updatedData.displayName || currentUser?.displayName || '',
            email: updatedData.email || currentUser?.email || '',
            photoURL: updatedData.photoURL || '',
         });

         setProfileData((prev) => ({
         ...prev,
            ...updatedData,
      }));

      setSuccessMessage('Profile updated successfully');
      }
   };

   const handleCoverPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      try {
         if (!e.target.files || e.target.files.length === 0) {
            return;
         }

         const file = e.target.files[0];
         setCoverPhotoURL(file);

         const storage = getStorage();
         const storageRef = ref(storage, `cover_photos/${currentUser?.uid}`);

         await uploadBytes(storageRef, file);

         const coverPhotoURL = await getDownloadURL(storageRef);

         await updateDoc(doc(db, 'userProfiles', currentUser?.uid || ''), {
            coverPhotoURL,
         });

         setProfileData((prev) => ({ ...prev, coverPhotoURL }));
      } catch (err) {
         console.error('Error uploading cover photo:', err);
         setError('Failed to upload cover photo');
      }
   };

   const handleDownloadBusinessCard = async () => {
      if (!currentUser?.uid) return;

      setDownloading('business-card');
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/profile/${currentUser.uid}/business-card`, {
            method: 'GET',
            headers,
         });

         if (!response.ok) {
            throw new Error('Failed to download business card');
         }

         const blob = await response.blob();
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `${currentUser?.displayName || 'Business Card'}.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
      } catch (error) {
         console.error('Error downloading business card:', error);
         setError('Failed to download business card');
      } finally {
         setDownloading(null);
      }
   };

   const handleDownloadProfile = async () => {
      if (!currentUser?.uid) return;

      setDownloading('profile');
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/profile/${currentUser.uid}/profile-pdf`, {
            method: 'GET',
            headers,
         });

         if (!response.ok) {
            throw new Error('Failed to download profile');
         }

         const blob = await response.blob();
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `${currentUser?.displayName || 'Profile'}.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
      } catch (error) {
         console.error('Error downloading profile:', error);
         setError('Failed to download profile');
      } finally {
         setDownloading(null);
      }
   };

   if (!currentUser) {
      navigate('/login');
      return null;
   }

   return (
      <Box
         sx={{
            width: '100%',
            minHeight: '100vh',
            height: 'auto',
            overflowY: 'auto',
            pb: isMobile ? 8 : 0,
            display: 'flex',
            justifyContent: 'center',
         }}
      >
         <Container
            maxWidth={isMobile ? false : 'lg'}
            disableGutters={isMobile}
            sx={{
               pt: isMobile ? 1 : 2,
               pb: isMobile ? 8 : 6,
               px: 0,
               width: '100%',
               maxWidth: isMobile ? '100vw' : '100%',
               mx: 0,
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'stretch',
               boxSizing: 'border-box',
               overflow: 'visible',
            }}
         >
            {/* Modern Profile Header */}
            <Box sx={{ px: isMobile ? 1 : 0, width: '100%', boxSizing: 'border-box' }}>
               <Paper
                  elevation={0}
                  sx={{
                     borderRadius: isMobile ? '8px' : 2,
                     overflow: 'hidden',
                     mb: isMobile ? 1 : 4,
                     position: 'relative',
                     width: '100%',
                     maxWidth: '100%',
                     background: theme.palette.primary.main,
                     color: theme.palette.primary.contrastText,
                     boxSizing: 'border-box',
                  }}
               >
                  {/* Header Section with Gradient Background */}
                  <Box
                     sx={{
                        background: coverPhotoURL
                           ? `linear-gradient(135deg, ${theme.palette.primary.main}CC 0%, ${theme.palette.primary.dark}CC 100%), url(${URL.createObjectURL(coverPhotoURL)})`
                           : profileData.coverPhotoURL
                             ? `linear-gradient(135deg, ${theme.palette.primary.main}CC 0%, ${theme.palette.primary.dark}CC 100%), url(${profileData.coverPhotoURL})`
                             : theme.palette.primary.main,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        padding: isMobile ? '1.5rem 1rem' : '3rem 2rem',
                        textAlign: 'center',
                        position: 'relative',
                        width: '100%',
                        minHeight: isMobile ? '280px' : 'auto',
                     }}
                  >
                     {/* Action Buttons */}
                     <Box
                        sx={{
                           position: 'absolute',
                           top: isMobile ? 4 : 20,
                           right: isMobile ? 4 : 20,
                           display: 'flex',
                           gap: isMobile ? 0.5 : 1,
                           flexDirection: isMobile ? 'column' : 'row',
                           zIndex: 10,
                        }}
                     >
                        <Button
                           variant='contained'
                           startIcon={isMobile ? null : <EditIcon />}
                           onClick={handleEditProfileClick}
                           size={isMobile ? 'small' : 'medium'}
                           sx={{
                              fontSize: isMobile ? '0.65rem' : '0.875rem',
                              minHeight: isMobile ? '28px' : '40px',
                              minWidth: isMobile ? '50px' : 'auto',
                              px: isMobile ? 1 : 2.5,
                              py: isMobile ? 0.5 : 1,
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              color: 'white',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              fontWeight: 600,
                              borderRadius: '8px',
                              '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                 transform: 'translateY(-2px)',
                                 boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              },
                              transition: 'all 0.2s ease',
                           }}
                        >
                           {isMobile ? <EditIcon sx={{ fontSize: '1rem' }} /> : 'Edit Profile'}
                        </Button>
                        <Button
                           variant='contained'
                           startIcon={isMobile ? null : <PhotoCameraIcon />}
                           component='label'
                           size={isMobile ? 'small' : 'medium'}
                           sx={{
                              fontSize: isMobile ? '0.65rem' : '0.875rem',
                              minHeight: isMobile ? '28px' : '40px',
                              minWidth: isMobile ? '50px' : 'auto',
                              px: isMobile ? 1 : 2.5,
                              py: isMobile ? 0.5 : 1,
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              color: 'white',
                              fontWeight: 600,
                              borderRadius: '8px',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                 transform: 'translateY(-2px)',
                                 boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              },
                              transition: 'all 0.2s ease',
                           }}
                        >
                           {isMobile ? (
                              <PhotoCameraIcon sx={{ fontSize: '1rem' }} />
                           ) : (
                              'Upload Cover Photo'
                           )}
                           <input
                              type='file'
                              accept='image/*'
                              hidden
                              onChange={handleCoverPhotoUpload}
                           />
                        </Button>
                     </Box>

                     {/* Profile Picture */}
                     <Box
                        sx={{
                           position: 'relative',
                           display: 'flex',
                           justifyContent: 'center',
                           mb: isMobile ? 1 : 2,
                           width: '100%',
                           mt: isMobile ? 1 : 0,
                        }}
                     >
                        <Avatar
                           src={profileData.photoURL}
                           sx={{
                              width: isMobile ? 80 : 150,
                              height: isMobile ? 80 : 150,
                              border: isMobile ? '3px solid white' : '4px solid white',
                              fontSize: isMobile ? '1.5rem' : '2.5rem',
                              fontWeight: 'bold',
                              backgroundColor: theme.palette.background.paper,
                              color: theme.palette.text.secondary,
                              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                           }}
                        >
                           {!profileData.photoURL && formData.displayName?.charAt(0)?.toUpperCase()}
                        </Avatar>
                     </Box>

                     {/* Name and Title */}
                     <Typography
                        variant='h3'
                        fontWeight='bold'
                        gutterBottom
                        sx={{
                           fontSize: isMobile ? '1.1rem' : '1.8rem',
                           mb: 0.5,
                           textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                           textAlign: 'center',
                           px: isMobile ? 1 : 0,
                           wordBreak: 'break-word',
                           lineHeight: isMobile ? 1.3 : 1.5,
                        }}
                     >
                        {formData.displayName}
                     </Typography>

                     {(profileData.title || profileData.companyName) && (
                        <Typography
                           variant='h6'
                           sx={{
                              fontSize: isMobile ? '0.75rem' : '1.1rem',
                              opacity: 0.9,
                              mb: isMobile ? 0.5 : 1,
                              fontWeight: 400,
                              textAlign: 'center',
                              px: isMobile ? 1 : 0,
                              wordBreak: 'break-word',
                              lineHeight: isMobile ? 1.3 : 1.5,
                           }}
                        >
                           {profileData.title && profileData.companyName
                              ? `${profileData.title} | ${profileData.companyName}`
                              : profileData.title
                                ? profileData.title
                                : profileData.companyName}
                        </Typography>
                     )}

                     {/* Location */}
                     {profileData.location && (
                        <Typography
                           variant='body1'
                           sx={{
                              opacity: 0.8,
                              mb: isMobile ? 1 : 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                              fontSize: isMobile ? '0.75rem' : '1rem',
                              px: isMobile ? 1 : 0,
                           }}
                        >
                           📍 {profileData.location}
                        </Typography>
                     )}

                     {/* Social Links */}
                     <Box
                        sx={{
                           display: 'flex',
                           gap: isMobile ? 0.5 : 1,
                           justifyContent: 'center',
                           pt: isMobile ? 1 : 2,
                           borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                           flexWrap: 'wrap',
                           px: isMobile ? 1 : 0,
                           width: '100%',
                        }}
                     >
                        {socialData.linkedin && (
                           <IconButton
                              href={
                                 socialData.linkedin.startsWith('http')
                                    ? socialData.linkedin
                                    : `https://linkedin.com/in/${socialData.linkedin}`
                              }
                              target='_blank'
                              size={isMobile ? 'small' : 'medium'}
                              sx={{
                                 color: 'rgba(255, 255, 255, 0.8)',
                                 p: isMobile ? 0.5 : 1,
                                 borderRadius: '6px',
                                 '&:hover': {
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    transform: 'translateY(-2px)',
                                 },
                                 transition: 'all 0.2s ease',
                                 '& .MuiSvgIcon-root': {
                                    fontSize: isMobile ? '1rem' : '1.5rem',
                                 },
                              }}
                           >
                              <LinkedInIcon />
                           </IconButton>
                        )}
                        {socialData.twitter && (
                           <IconButton
                              href={
                                 socialData.twitter.startsWith('http')
                                    ? socialData.twitter
                                    : `https://twitter.com/${socialData.twitter}`
                              }
                              target='_blank'
                              size={isMobile ? 'small' : 'medium'}
                              sx={{
                                 color: 'rgba(255, 255, 255, 0.8)',
                                 p: isMobile ? 0.5 : 1,
                                 borderRadius: '6px',
                                 '&:hover': {
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    transform: 'translateY(-2px)',
                                 },
                                 transition: 'all 0.2s ease',
                                 '& .MuiSvgIcon-root': {
                                    fontSize: isMobile ? '1rem' : '1.5rem',
                                 },
                              }}
                           >
                              <TwitterIcon />
                           </IconButton>
                        )}
                        {socialData.facebook && (
                           <IconButton
                              href={
                                 socialData.facebook.startsWith('http')
                                    ? socialData.facebook
                                    : `https://facebook.com/${socialData.facebook}`
                              }
                              target='_blank'
                              size={isMobile ? 'small' : 'medium'}
                              sx={{
                                 color: 'rgba(255, 255, 255, 0.8)',
                                 p: isMobile ? 0.5 : 1,
                                 borderRadius: '6px',
                                 '&:hover': {
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    transform: 'translateY(-2px)',
                                 },
                                 transition: 'all 0.2s ease',
                                 '& .MuiSvgIcon-root': {
                                    fontSize: isMobile ? '1rem' : '1.5rem',
                                 },
                              }}
                           >
                              <FacebookIcon />
                           </IconButton>
                        )}
                        {socialData.instagram && (
                           <IconButton
                              href={
                                 socialData.instagram.startsWith('http')
                                    ? socialData.instagram
                                    : `https://instagram.com/${socialData.instagram}`
                              }
                              target='_blank'
                              size={isMobile ? 'small' : 'medium'}
                              sx={{
                                 color: 'rgba(255, 255, 255, 0.8)',
                                 p: isMobile ? 0.5 : 1,
                                 borderRadius: '6px',
                                 '&:hover': {
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    transform: 'translateY(-2px)',
                                 },
                                 transition: 'all 0.2s ease',
                                 '& .MuiSvgIcon-root': {
                                    fontSize: isMobile ? '1rem' : '1.5rem',
                                 },
                              }}
                           >
                              <InstagramIcon />
                           </IconButton>
                        )}
                        {profileData.website && (
                           <IconButton
                              href={
                                 profileData.website.startsWith('http')
                                    ? profileData.website
                                    : `https://${profileData.website}`
                              }
                              target='_blank'
                              size={isMobile ? 'small' : 'medium'}
                              sx={{
                                 color: 'rgba(255, 255, 255, 0.8)',
                                 p: isMobile ? 0.5 : 1,
                                 borderRadius: '6px',
                                 '&:hover': {
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    transform: 'translateY(-2px)',
                                 },
                                 transition: 'all 0.2s ease',
                                 '& .MuiSvgIcon-root': {
                                    fontSize: isMobile ? '1rem' : '1.5rem',
                                 },
                              }}
                           >
                              <LanguageIcon />
                           </IconButton>
                        )}
                        {profileData.phoneNumber && (
                           <IconButton
                              href={`tel:${profileData.phoneNumber}`}
                              size={isMobile ? 'small' : 'medium'}
                              sx={{
                                 color: 'rgba(255, 255, 255, 0.8)',
                                 p: isMobile ? 0.5 : 1,
                                 borderRadius: '6px',
                                 '&:hover': {
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    transform: 'translateY(-2px)',
                                 },
                                 transition: 'all 0.2s ease',
                                 '& .MuiSvgIcon-root': {
                                    fontSize: isMobile ? '1rem' : '1.5rem',
                                 },
                              }}
                           >
                              <PhoneIcon />
                           </IconButton>
                        )}
                     </Box>
                  </Box>
               </Paper>
            </Box>

            {/* Tab Navigation - EXACT structure as AlbumDetailPage */}
            <Box
               sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  width: '100%',
                  maxWidth: '100%',
                  flexShrink: 0,
               }}
            >
               <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant='scrollable'
                  scrollButtons='auto'
                  allowScrollButtonsMobile
                  selectionFollowsFocus={false}
                  TabIndicatorProps={{
                     sx: { height: 3, borderRadius: '3px 3px 0 0' },
                  }}
                  sx={{
                     '& .MuiTabs-scroller': {
                        overflow: 'auto !important',
                     },
                     '& .MuiTabs-flexContainer': {
                        gap: isMobile ? '4px' : '8px',
                        pr: isMobile ? 2 : 0,
                     },
                     '& .MuiTab-root': {
                        minHeight: isMobile ? 48 : 48,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: isMobile ? '0.8rem' : '1rem',
                        padding: isMobile ? '12px 16px' : '12px 16px',
                        minWidth: isMobile ? 'auto' : 'auto',
                        opacity: 1,
                        color: 'text.primary',
                        '&.Mui-selected': {
                           color: 'primary.main',
                        },
                        '&:not(.Mui-selected)': {
                           opacity: 0.7,
                        },
                     },
                     '& .MuiTabs-scrollButtons': {
                        '&.Mui-disabled': {
                           opacity: 0.3,
                        },
                     },
                  }}
               >
                  <Tab label='Overview' />
                  <Tab label='Network' />
                  <Tab label='Photos' />
                  <Tab label='QR Code' />
                  <Tab label={isMobile ? 'Analytics' : 'Profile Analytics'} />
                  <Tab label={isMobile ? 'Settings' : 'Account Settings'} />
               </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ px: isMobile ? 1 : 0, width: '100%', boxSizing: 'border-box' }}>
               <Grid
                  container
                  spacing={isMobile ? 0 : 3}
                  sx={{
                     px: 0,
                     width: '100%',
                     maxWidth: '100%',
                     m: 0,
                     justifyContent: 'center',
                     boxSizing: 'border-box',
                  }}
               >
                  {activeTab === 0 && (
                     <>
                        {/* About Section */}
                        <Grid
                           item
                           xs={12}
                           md={12}
                           sx={{ width: '100%', maxWidth: '100%', px: 0, pl: 0 }}
                        >
                           <Card
                              sx={{
                                 borderRadius: isMobile ? '8px' : 2,
                                 boxShadow: isMobile ? 1 : 2,
                                 width: '100%',
                                 mx: 0,
                                 boxSizing: 'border-box',
                              }}
                           >
                              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                 <Typography
                                    variant='h6'
                                    gutterBottom
                                    sx={{
                                       fontSize: isMobile ? fontSize.h5 : fontSize.h4,
                                       textAlign: isMobile ? 'center' : 'left',
                                    }}
                                 >
                                    Overview
                                 </Typography>
                                 <Divider sx={{ mb: isMobile ? 1.5 : 2 }} />

                                 <Grid
                                    container
                                    spacing={isMobile ? 1 : 2}
                                    sx={{ width: '100%', m: 0 }}
                                 >
                                    <Grid item xs={12} sm={4}>
                                       <Typography
                                          variant='body2'
                                          color='text.secondary'
                                          sx={{
                                             fontSize: isMobile ? '0.75rem' : '0.875rem',
                                             fontWeight: isMobile ? 600 : 400,
                                          }}
                                       >
                                          Full Name
                                       </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                       <Typography
                                          variant='body1'
                                          sx={{
                                             fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                                             wordBreak: 'break-word',
                                             overflowWrap: 'anywhere',
                                          }}
                                       >
                                          {formData.displayName}
                                       </Typography>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                       <Typography
                                          variant='body2'
                                          color='text.secondary'
                                          sx={{
                                             fontSize: isMobile ? '0.75rem' : '0.875rem',
                                             fontWeight: isMobile ? 600 : 400,
                                          }}
                                       >
                                          Email
                                       </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                       <Typography
                                          variant='body1'
                                          sx={{
                                             fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                                             wordBreak: 'break-word',
                                          }}
                                       >
                                          {formData.email}
                                       </Typography>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                       <Typography
                                          variant='body2'
                                          color='text.secondary'
                                          sx={{
                                             fontSize: isMobile ? '0.75rem' : '0.875rem',
                                             fontWeight: isMobile ? 600 : 400,
                                          }}
                                       >
                                          Location
                                       </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                       <Typography
                                          variant='body1'
                                          sx={{
                                             fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                                             wordBreak: 'break-word',
                                          }}
                                       >
                                          {profileData.location}
                                       </Typography>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                       <Typography
                                          variant='body2'
                                          color='text.secondary'
                                          sx={{
                                             fontSize: isMobile ? '0.75rem' : '0.875rem',
                                             fontWeight: isMobile ? 600 : 400,
                                          }}
                                       >
                                          Languages
                                       </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                       <Typography
                                          variant='body1'
                                          sx={{
                                             fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                                             wordBreak: 'break-word',
                                          }}
                                       >
                                          {profileData.languages}
                                       </Typography>
                                    </Grid>

                                    {/* Tagline */}
                                    {profileData.tagline && (
                                       <>
                                          <Grid item xs={12} sm={4}>
                                             <Typography
                                                variant='body2'
                                                color='text.secondary'
                                                sx={{
                                                   fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                   fontWeight: isMobile ? 600 : 400,
                                                }}
                                             >
                                                Tagline
                                             </Typography>
                                          </Grid>
                                          <Grid item xs={12} sm={8}>
                                             <Typography
                                                variant='body1'
                                                sx={{
                                                   fontSize: isMobile
                                                      ? fontSize.body2
                                                      : fontSize.body1,
                                                   wordBreak: 'break-word',
                                                }}
                                             >
                                                {profileData.tagline}
                                             </Typography>
                                          </Grid>
                                       </>
                                    )}

                                    {/* Company */}
                                    {profileData.companyName && (
                                       <>
                                          <Grid item xs={12} sm={4}>
                                             <Typography
                                                variant='body2'
                                                color='text.secondary'
                                                sx={{
                                                   fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                   fontWeight: isMobile ? 600 : 400,
                                                }}
                                             >
                                                Company
                                             </Typography>
                                          </Grid>
                                          <Grid item xs={12} sm={8}>
                                             <Typography
                                                variant='body1'
                                                sx={{
                                                   fontSize: isMobile
                                                      ? fontSize.body2
                                                      : fontSize.body1,
                                                   wordBreak: 'break-word',
                                                }}
                                             >
                                                {profileData.companyName}
                                             </Typography>
                                          </Grid>
                                       </>
                                    )}

                                    {/* What I Do */}
                                    {profileData.whatIDo && profileData.whatIDo.length > 0 && (
                                       <>
                                          <Grid item xs={12} sm={4}>
                                             <Typography
                                                variant='body2'
                                                color='text.secondary'
                                                sx={{
                                                   fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                   fontWeight: isMobile ? 600 : 400,
                                                }}
                                             >
                                                What I Do
                                             </Typography>
                                          </Grid>
                                          <Grid item xs={12} sm={8}>
                                             <Box
                                                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                                             >
                                                {profileData.whatIDo.map((item, index) => (
                                                   <Chip
                                                      key={index}
                                                      label={item.title}
                                                      variant='outlined'
                                                      sx={{
                                                         borderColor: '#e2e8f0',
                                                         color: '#64748b',
                                                         fontWeight: 500,
                                                         '&:hover': {
                                                            borderColor: '#00C4A7',
                                                            color: '#00C4A7',
                                                         },
                                                      }}
                                                   />
                                                ))}
                                             </Box>
                                          </Grid>
                                       </>
                                    )}

                                    {/* Interests */}
                                    {profileData.interests && profileData.interests.length > 0 && (
                                       <>
                                          <Grid item xs={12} sm={4}>
                                             <Typography
                                                variant='body2'
                                                color='text.secondary'
                                                sx={{
                                                   fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                   fontWeight: isMobile ? 600 : 400,
                                                }}
                                             >
                                                Interests
                                             </Typography>
                                          </Grid>
                                          <Grid item xs={12} sm={8}>
                                             <Box
                                                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                                             >
                                                {profileData.interests
                                                   .filter(
                                                      (interest) =>
                                                         interest.selected || interest.isCustom
                                                   )
                                                   .map((interest, index) => (
                                                      <Chip
                                                         key={index}
                                                         label={interest.name}
                                                         icon={<span>{interest.emoji}</span>}
                                                         variant='outlined'
                                                         sx={{
                                                            borderColor: theme.palette.divider,
                                                             color: theme.palette.text.secondary,
                                                            fontWeight: 500,
                                                            '&:hover': {
                                                               borderColor: theme.palette.primary.main,
                                                               color: theme.palette.primary.main,
                                                            },
                                                         }}
                                                      />
                                                   ))}
                                             </Box>
                                          </Grid>
                                       </>
                                    )}

                                    {/* Looking For */}
                                    {profileData.lookingFor &&
                                       profileData.lookingFor.length > 0 && (
                                          <>
                                             <Grid item xs={12} sm={4}>
                                                <Typography
                                                   variant='body2'
                                                   color='text.secondary'
                                                   sx={{
                                                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                      fontWeight: isMobile ? 600 : 400,
                                                   }}
                                                >
                                                   Looking For
                                                </Typography>
                                             </Grid>
                                             <Grid item xs={12} sm={8}>
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      flexWrap: 'wrap',
                                                      gap: 1,
                                                   }}
                                                >
                                                   {profileData.lookingFor.map((item, index) => (
                                                      <Chip
                                                         key={index}
                                                         label={item.title}
                                                         variant='outlined'
                                                         sx={{
                                                            borderColor: theme.palette.divider,
                                                             color: theme.palette.text.secondary,
                                                            fontWeight: 500,
                                                            '&:hover': {
                                                               borderColor: theme.palette.primary.main,
                                                               color: theme.palette.primary.main,
                                                            },
                                                         }}
                                                      />
                                                   ))}
                                                </Box>
                                             </Grid>
                                          </>
                                       )}

                                    {/* Featured Projects */}
                                    {profileData.featuredProjects &&
                                       profileData.featuredProjects.length > 0 && (
                                          <>
                                             <Grid item xs={12} sm={4}>
                                                <Typography
                                                   variant='body2'
                                                   color='text.secondary'
                                                   sx={{
                                                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                      fontWeight: isMobile ? 600 : 400,
                                                   }}
                                                >
                                                   Featured Projects
                                                </Typography>
                                             </Grid>
                                             <Grid item xs={12} sm={8}>
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      flexWrap: 'wrap',
                                                      gap: 1,
                                                   }}
                                                >
                                                   {profileData.featuredProjects.map(
                                                      (project, index) => (
                                                         <Chip
                                                            key={index}
                                                            label={project.title}
                                                            icon={<span>⭐</span>}
                                                            variant='outlined'
                                                            sx={{
                                                               borderColor: '#e2e8f0',
                                                               color: '#64748b',
                                                               fontWeight: 500,
                                                               '&:hover': {
                                                                  borderColor: '#00C4A7',
                                                                  color: '#00C4A7',
                                                               },
                                                            }}
                                                         />
                                                      )
                                                   )}
                                                </Box>
                                             </Grid>
                                          </>
                                       )}

                                    <Grid item xs={12}>
                                       <Typography
                                          variant='body2'
                                          color='text.secondary'
                                          sx={{
                                             fontSize: isMobile ? '0.75rem' : '0.875rem',
                                             fontWeight: isMobile ? 600 : 400,
                                             mb: isMobile ? 0.5 : 1,
                                          }}
                                       >
                                          Bio
                                       </Typography>
                                       <Typography
                                          variant='body1'
                                          sx={{
                                             mt: isMobile ? 0.5 : 1,
                                             fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                                             wordBreak: 'break-word',
                                             lineHeight: isMobile ? 1.4 : 1.6,
                                          }}
                                          dangerouslySetInnerHTML={{
                                             __html: profileData.bio || '',
                                          }}
                                       />
                                    </Grid>
                                 </Grid>
                              </CardContent>
                           </Card>
                        </Grid>

                        {/* Stats Section */}
                        {/* <Grid item xs={12} md={5}>
                     <Card sx={{ borderRadius: 2, mb: 3 }}>
                        <CardContent>
                           <Typography variant='h6' gutterBottom>
                              Travel Stats
                           </Typography>
                           <Divider sx={{ mb: 2 }} />

                           <Grid container spacing={2}>
                              <Grid item xs={4} textAlign='center'>
                                 <Typography variant='h4' color='primary'>
                                    12
                                 </Typography>
                                 <Typography variant='body2' color='text.secondary'>
                                    Trips
                                 </Typography>
                              </Grid>
                              <Grid item xs={4} textAlign='center'>
                                 <Typography variant='h4' color='primary'>
                                    8
                                 </Typography>
                                 <Typography variant='body2' color='text.secondary'>
                                    Countries
                                 </Typography>
                              </Grid>
                              <Grid item xs={4} textAlign='center'>
                                 <Typography variant='h4' color='primary'>
                                    24
                                 </Typography>
                                 <Typography variant='body2' color='text.secondary'>
                                    Cities
                                 </Typography>
                              </Grid>
                           </Grid>
                        </CardContent>
                     </Card>

                     <Card sx={{ borderRadius: 2 }}>
                        <CardContent>
                           <Typography variant='h6' gutterBottom>
                              Network
                           </Typography>
                           <Divider sx={{ mb: 2 }} />

                           <Grid container spacing={2}>
                              <Grid item xs={6} textAlign='center'>
                                 <Typography variant='h4' color='primary'>
                                    45
                                 </Typography>
                                 <Typography variant='body2' color='text.secondary'>
                                    Connections
                                 </Typography>
                              </Grid>
                              <Grid item xs={6} textAlign='center'>
                                 <Typography variant='h4' color='primary'>
                                    3
                                 </Typography>
                                 <Typography variant='body2' color='text.secondary'>
                                    Pending
                                 </Typography>
                              </Grid>
                           </Grid>
                        </CardContent>
                     </Card>
                  </Grid> */}
                     </>
                  )}
                  {/* {activeTab === 1 && (
               <Grid item xs={12}>
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                     <FeedPage userId={currentUser.uid} />
                  </Paper>
               </Grid>
            )} */}
                  {activeTab === 1 && (
                     <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%', px: 0, pl: 0 }}>
                        <ProfileNetwork userId={currentUser.uid} />
                     </Grid>
                  )}
                  {activeTab === 2 && (
                     <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%', px: 0, pl: 0 }}>
                        <Paper
                           sx={{
                              p: isMobile ? 2 : 3,
                              borderRadius: isMobile ? '8px' : 2,
                              boxShadow: isMobile ? 1 : 2,
                              width: '100%',
                              mx: 0,
                              boxSizing: 'border-box',
                           }}
                        >
                           <ProfilePhotos
                              userId={currentUser.uid}
                              currentProfilePhoto={currentUser.photoURL || undefined}
                              onProfilePhotoUpdate={async (url: string) => {
                                 // Update immediately for instant feedback
                                 setFormData((prev: any) => ({ ...prev, photoURL: url }));
                                 setProfileData((prev) => ({ ...prev, photoURL: url }));

                                 // Fetch fresh data from backend to sync everywhere
                                 try {
                                    const token = await auth.currentUser?.getIdToken();
                                    const response = await fetch(
                                       `${process.env.REACT_APP_API_URL}/api/profile/${currentUser.uid}`,
                                       {
                                          headers: { Authorization: `Bearer ${token}` },
                                       }
                                    );
                                    if (response.ok) {
                                       const freshData = await response.json();
                                       setFormData((prev: any) => ({
                                          ...prev,
                                          photoURL: freshData.photoURL,
                                       }));
                                       setProfileData((prev) => ({
                                          ...prev,
                                          photoURL: freshData.photoURL,
                                       }));
                                       console.log('✅ Profile photo synced from backend');
                                    }
                                 } catch (error) {
                                    console.error('Error syncing profile photo:', error);
                                 }
                              }}
                              onCoverPhotoUpdate={(url: string) => {
                                 setProfileData((prev) => ({ ...prev, coverPhotoURL: url }));
                              }}
                           />
                        </Paper>
                     </Grid>
                  )}
                  {/* {activeTab === 3 && (
               <Grid item xs={12}>
                  <LandingPageEditor key={landingPageKey} userId={currentUser.uid} />
               </Grid>
            )} */}
                  {activeTab === 3 && (
                     <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%', px: 0, pl: 0 }}>
                        <Paper
                           sx={{
                              p: isMobile ? 2 : 3,
                              borderRadius: isMobile ? '8px' : 2,
                              boxShadow: isMobile ? 1 : 2,
                              width: '100%',
                              mx: 0,
                              boxSizing: 'border-box',
                           }}
                        >
                           <ProfileQRCode userId={currentUser.uid} />
                        </Paper>
                     </Grid>
                  )}
                  {activeTab === 4 && (
                     <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%', px: 0, pl: 0 }}>
                        <Paper
                           sx={{
                              p: isMobile ? 2 : 3,
                              borderRadius: isMobile ? '8px' : 2,
                              boxShadow: isMobile ? 1 : 2,
                              width: '100%',
                              mx: 0,
                              boxSizing: 'border-box',
                           }}
                        >
                           <ProfileBusinessCardTracking userId={currentUser.uid} />
                        </Paper>
                     </Grid>
                  )}
                  {activeTab === 5 && (
                     <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%', px: 0, pl: 0 }}>
                        <Paper
                           sx={{
                              p: isMobile ? 2 : 3,
                              borderRadius: isMobile ? '8px' : 2,
                              boxShadow: isMobile ? 1 : 2,
                              width: '100%',
                              mx: 0,
                              boxSizing: 'border-box',
                           }}
                        >
                           <Typography
                              variant='h6'
                              sx={{
                                 mb: 2,
                                 display: 'flex',
                                 alignItems: 'center',
                              }}
                           >
                              <PhoneIcon sx={{ mr: 1 }} />
                              Account Settings
                           </Typography>
                           <PhoneNumberLinking userId={currentUser.uid} />
                        </Paper>
                     </Grid>
                  )}
               </Grid>
            </Box>

            {/* Edit Profile Dialog */}
            <EditProfileDialog
               open={editDialogOpen}
               onClose={handleEditDialogClose}
               userId={currentUser.uid}
               profileData={{
                  displayName: formData.displayName,
                  email: formData.email,
                  bio: profileData.bio,
                  location: profileData.location,
                  birthdate: profileData.birthdate,
                  languages: profileData.languages,
                  website: profileData.website,
                  phoneNumber: profileData.phoneNumber,
                  title: profileData.title,
                  companyName: profileData.companyName,
                  facebook: socialData.facebook,
                  twitter: socialData.twitter,
                  linkedin: socialData.linkedin,
                  instagram: socialData.instagram,
                  tagline: profileData.tagline,
                  whatIDo: profileData.whatIDo,
                  interests: profileData.interests,
                  lookingFor: profileData.lookingFor,
                  featuredProjects: profileData.featuredProjects,
               }}
               onProfileUpdated={handleProfileUpdated}
            />
         </Container>
      </Box>
   );
};

export default Profile;
